import { decode } from 'html-entities';
import { useEffect, useRef, useState } from 'react';

import { formatPrice } from 'src/utils/formatPrice';
import { localUrl } from 'src/utils';
import type { Deal } from 'src/types';

import { vendorFaviconUrl } from '../constants';
import type { MakeItem } from '../makeApi';
import { cheapestFirst, normalizePriceToNumber } from '../priceUtils';
import type { PriceQuote } from './tools';
import { usePromptApiChat } from './usePromptApiChat';

interface Props {
  onAddItems: (items: MakeItem[]) => void;
  onSearch: (
    vendor: string,
    keywords: string,
    region?: string,
    zip?: string,
  ) => Promise<MakeItem[]>;
  cartCount: number;
  onSave: () => void;
  getCartItems: () => MakeItem[];
  onRemoveIndexes: (indexes: number[]) => void;
  onSetQuantity: (index: number, qty: number) => void;
  onClearCart: () => void;
  vendor?: string;
}

const EMPTY_SUGGESTIONS = [
  'Search Amazon for wireless headphones',
  'Find deals at Target',
  'What can you do?',
];

const CART_SUGGESTIONS = ['Compare my cart', 'Any deals for my cart?', 'What can you do?'];

const UNAVAILABLE_IMG = 'https://assets.share-a-cart.com/images/next/unavailable.svg';

const Avatar = ({ size = 'h-8 w-8' }: { size?: string }) => (
  <span
    className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dark text-white ring-2 ring-white`}
  >
    <span className='material-icons-outlined text-[16px] leading-none'>auto_awesome</span>
  </span>
);

// Lightweight inline formatting: **bold**, `code`, and links.
const renderInline = (text: string, base: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*|`[^`]+`|https?:\/\/[^\s]+)/g).map((part, i) => {
    const key = `${base}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(part))
      return (
        <code key={key} className='rounded bg-black/5 px-1 text-[0.9em]'>
          {part.slice(1, -1)}
        </code>
      );
    if (/^https?:\/\//.test(part))
      return (
        <a
          key={key}
          href={part}
          target='_blank'
          rel='noopener noreferrer'
          className='break-all font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary'
        >
          {part}
        </a>
      );
    return <span key={key}>{part}</span>;
  });

// Renders an assistant message with light markdown (bold, code, links, bullets).
const renderRich = (text: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let bullets: React.ReactNode[] = [];
  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${out.length}`} className='my-1 list-disc space-y-0.5 pl-4'>
          {bullets}
        </ul>,
      );
      bullets = [];
    }
  };
  text.split('\n').forEach((line, i) => {
    const b = line.match(/^\s*[-*]\s+(.*)$/);
    if (b) {
      bullets.push(<li key={`li-${i}`}>{renderInline(b[1], `li-${i}`)}</li>);
    } else {
      flush();
      if (line.trim()) out.push(<p key={`p-${i}`}>{renderInline(line, `p-${i}`)}</p>);
    }
  });
  flush();
  return out;
};

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

const priceLabel = (it: MakeItem) => {
  const n = normalizePriceToNumber(it.price, 'us');
  return n
    ? formatPrice(n, 'us', it.priceccy || it.ccy || 'USD', it.priceccyS || it.ccyS || '$', false)
    : '';
};

const cheapestOf = (items: MakeItem[]): MakeItem | undefined => cheapestFirst(items)[0];

const Chip = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button
    type='button'
    onClick={onClick}
    className='rounded-full border border-primary/20 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10'
  >
    {children}
  </button>
);

const ResultCard = ({
  item,
  number,
  added,
  onAdd,
}: {
  item: MakeItem;
  number: number;
  added: boolean;
  onAdd: () => void;
}) => (
  <div className='flex items-center gap-2.5 rounded-2xl border border-black/5 bg-white p-2.5 shadow-sm'>
    <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white'>
      {number}
    </span>
    <img
      src={item.image || UNAVAILABLE_IMG}
      alt=''
      loading='lazy'
      className='h-12 w-12 shrink-0 rounded-lg object-contain'
    />
    <div className='min-w-0 flex-1'>
      <p className='line-clamp-2 text-xs font-semibold leading-snug text-primary-dark'>
        {item.title ? decode(item.title) : item.asin}
      </p>
      {priceLabel(item) && <p className='text-xs text-offGray'>{priceLabel(item)}</p>}
    </div>
    <button
      type='button'
      onClick={onAdd}
      disabled={added}
      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition ${
        added ? 'bg-grassGreen/15 text-grassGreen' : 'bg-primary text-white hover:bg-primary-hover'
      }`}
    >
      {added ? '✓' : 'Add'}
    </button>
  </div>
);

const dealClickHref = (deal: Deal) =>
  `/deal-click/${encodeURIComponent(deal.retailer)}/${encodeURIComponent(
    deal.linkTemplate ?? deal.link,
  )}/web${deal.wrapAffil ? '?wrapAffil=true' : ''}`;

const dealExpiry = (deal: Deal): string | null => {
  if (!deal.expires_at) return null;
  const d = new Date(deal.expires_at);
  return Number.isNaN(d.getTime())
    ? null
    : `exp ${d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}`;
};

const DealRow = ({ deal }: { deal: Deal }) => {
  const meta = [deal.code ? `Code ${deal.code}` : null, dealExpiry(deal)]
    .filter(Boolean)
    .join(' · ');
  return (
    <a
      href={dealClickHref(deal)}
      target='_blank'
      rel='sponsored nofollow noopener noreferrer'
      className='flex items-center gap-2.5 rounded-2xl border border-black/5 bg-white p-2.5 shadow-sm transition hover:bg-gray-50'
    >
      <img
        src={deal.image_url || vendorFaviconUrl(deal.retailer)}
        alt=''
        loading='lazy'
        className='h-10 w-10 shrink-0 rounded-lg object-contain'
      />
      <div className='min-w-0 flex-1'>
        <p className='line-clamp-1 text-xs font-semibold leading-snug text-primary-dark'>
          {deal.name}
        </p>
        {deal.description && (
          <p className='line-clamp-2 text-[11px] leading-snug text-gray-600'>
            {deal.description}
          </p>
        )}
        {meta && <p className='mt-0.5 text-[11px] text-offGray'>{meta}</p>}
      </div>
      <span className='shrink-0 self-center text-xs font-bold text-primary'>Get offer →</span>
    </a>
  );
};

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const QuoteRow = ({
  quote,
  added,
  onAdd,
}: {
  quote: PriceQuote;
  added: boolean;
  onAdd: () => void;
}) => (
  <div
    className={`flex items-center gap-2.5 rounded-2xl border bg-white p-2.5 shadow-sm ${
      quote.cheapest ? 'border-grassGreen ring-1 ring-grassGreen/40' : 'border-black/5'
    }`}
  >
    <img
      src={quote.item?.image || vendorFaviconUrl(quote.vendor)}
      alt=''
      loading='lazy'
      className='h-10 w-10 shrink-0 rounded-lg object-contain'
    />
    <div className='min-w-0 flex-1'>
      <p className='flex flex-wrap items-center gap-1.5 text-xs font-semibold text-primary-dark'>
        {quote.inCart ? 'Your cart' : titleCase(quote.vendor)}
        {quote.cheapest && (
          <span className='rounded-full bg-grassGreen/15 px-1.5 py-0.5 text-[10px] font-bold text-grassGreen'>
            {quote.inCart ? 'Best price' : 'Cheapest'}
          </span>
        )}
        {quote.note && (
          <span className='rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700'>
            {quote.note}
          </span>
        )}
      </p>
      {quote.item && (
        <p className='line-clamp-1 text-[11px] text-gray-600'>
          {quote.item.title ? decode(quote.item.title) : quote.item.asin}
        </p>
      )}
    </div>
    <div className='flex shrink-0 flex-col items-end gap-1'>
      <span className='text-xs font-bold text-primary-dark'>{quote.priceLabel}</span>
      {quote.inCart ? (
        <span className='text-[10px] font-semibold text-offGray'>In cart</span>
      ) : quote.item ? (
        <button
          type='button'
          onClick={onAdd}
          disabled={added}
          className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
            added ? 'bg-grassGreen/15 text-grassGreen' : 'bg-primary text-white hover:bg-primary-hover'
          }`}
        >
          {added ? '✓' : 'Add'}
        </button>
      ) : null}
    </div>
  </div>
);

export const MakeChat = ({
  onAddItems,
  onSearch,
  cartCount,
  onSave,
  getCartItems,
  onRemoveIndexes,
  onSetQuantity,
  onClearCart,
  vendor,
}: Props) => {
  const { status, messages, busy, activity, downloadProgress, send, stop, newChat } = usePromptApiChat({
    onAddItems,
    onSearch,
    getCartItems,
    onRemoveIndexes,
    onSetQuantity,
    onClearCart,
    onSaveCart: onSave,
    vendor,
  });
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [added, setAdded] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  if (status === 'checking' || status === 'unsupported' || status === 'unavailable') {
    return null;
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input;
    setInput('');
    void send(text);
  };

  const addOne = (item: MakeItem) => {
    onAddItems([item]);
    setAdded((prev) => new Set(prev).add(item.asin));
  };

  if (!open) {
    return (
      <button
        type='button'
        aria-label='Open shopping assistant'
        onClick={() => setOpen(true)}
        className='group fixed bottom-6 right-6 z-40 flex items-center gap-2.5 rounded-full bg-gradient-to-br from-primary to-primary-dark py-3 pl-4 pr-5 font-primary font-semibold text-white shadow-[0_8px_30px_rgba(48,50,220,0.35)] ring-1 ring-white/20 transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_44px_rgba(48,50,220,0.5)]'
      >
        <span className='relative flex h-6 w-6 items-center justify-center'>
          <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-white/40' />
          <span className='material-icons-outlined relative text-[20px] leading-none'>
            auto_awesome
          </span>
        </span>
        Ask AI
      </button>
    );
  }

  return (
    <div className='fixed inset-x-3 bottom-3 z-50 flex max-h-[85vh] animate-modalIn flex-col overflow-hidden rounded-3xl bg-offWhite shadow-2xl ring-1 ring-black/10 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:h-[640px] sm:w-[420px]'>
      {/* Header */}
      <header className='relative overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-primary px-4 py-3.5 text-white'>
        <span className='pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl' />
        <div className='relative flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2.5'>
            <Avatar size='h-9 w-9' />
            <div>
              <p className='font-primary font-semibold leading-tight'>Shopping assistant</p>
              <p className='flex items-center gap-1.5 text-xs text-white/70'>
                <span className='inline-block h-1.5 w-1.5 rounded-full bg-grassGreen' />
                On-device · private
              </p>
            </div>
          </div>
          <div className='flex items-center gap-1.5'>
            <button
              type='button'
              onClick={onSave}
              disabled={cartCount === 0}
              className='flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-sm font-semibold transition hover:bg-white/25 disabled:opacity-40'
            >
              <span className='material-icons-outlined text-[18px] leading-none'>shopping_cart</span>
              {cartCount > 0 ? `Save (${cartCount})` : 'Save'}
            </button>
            {messages.length > 0 && (
              <button
                type='button'
                aria-label='New chat'
                title='New chat'
                onClick={() => {
                  newChat();
                  setAdded(new Set());
                  setInput('');
                }}
                className='material-icons-outlined rounded-full p-1.5 text-[20px] leading-none transition hover:bg-white/15'
              >
                edit_square
              </button>
            )}
            <button
              type='button'
              aria-label='Close assistant'
              onClick={() => setOpen(false)}
              className='material-icons-outlined rounded-full p-1.5 text-[20px] leading-none transition hover:bg-white/15'
            >
              close
            </button>
          </div>
        </div>
      </header>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-5 font-secondary'
      >
        {messages.length === 0 && (
          <div className='flex flex-col items-center gap-4 px-2 pt-6 text-center'>
            <Avatar size='h-14 w-14' />
            <div>
              <p className='font-primary text-lg font-semibold text-primary-dark'>
                Let’s build your list
              </p>
              <p className='mt-1 text-sm text-offGray'>
                Search any retailer and I’ll drop items into your cart.
              </p>
            </div>
            <div className='flex flex-wrap justify-center gap-2'>
              {(cartCount > 0 ? CART_SUGGESTIONS : EMPTY_SUGGESTIONS).map((s) => (
                <button
                  key={s}
                  type='button'
                  onClick={() => void send(s)}
                  className='rounded-full border border-primary/20 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10'
                >
                  {s}
                </button>
              ))}
            </div>
            <p className='mt-1 max-w-[290px] text-[11px] leading-relaxed text-offGray'>
              This runs on Chrome’s built-in AI — a small on-device model, so it can be slow or
              imprecise. For a faster, more capable assistant, connect the{' '}
              <a
                href={localUrl('/developers')}
                target='_blank'
                rel='noopener noreferrer'
                className='font-semibold text-primary underline hover:opacity-80'
              >
                Share-A-Cart MCP or agent skill
              </a>
              .
            </p>
          </div>
        )}

        {messages.map((m) => {
          if (m.items) {
            return (
              <div key={m.id} className='flex animate-translateIn items-start gap-2'>
                <Avatar />
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  {m.items.map((it, i) => (
                    <ResultCard
                      key={`${it.asin}-${i}`}
                      item={it}
                      number={i + 1}
                      added={added.has(it.asin)}
                      onAdd={() => addOne(it)}
                    />
                  ))}
                </div>
              </div>
            );
          }
          if (m.deals) {
            return (
              <div key={m.id} className='flex animate-translateIn items-start gap-2'>
                <Avatar />
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  {m.deals.map((d, i) => (
                    <DealRow key={`${d.retailer}-${i}`} deal={d} />
                  ))}
                </div>
              </div>
            );
          }
          if (m.comparison) {
            return (
              <div key={m.id} className='flex animate-translateIn items-start gap-2'>
                <Avatar />
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <p className='line-clamp-1 text-[11px] font-semibold uppercase tracking-wide text-offGray'>
                    Price comparison · {m.comparison.query}
                  </p>
                  {m.comparison.quotes.map((q, i) => (
                    <QuoteRow
                      key={`${q.vendor}-${i}`}
                      quote={q}
                      added={!!q.item && added.has(q.item.asin)}
                      onAdd={() => q.item && addOne(q.item)}
                    />
                  ))}
                  {m.comparison.caveat && (
                    <p className='text-[11px] leading-snug text-amber-600'>
                      ⚠ {m.comparison.caveat}
                    </p>
                  )}
                </div>
              </div>
            );
          }
          if (m.cartSavings) {
            const { rows, totalSavings } = m.cartSavings;
            return (
              <div key={m.id} className='flex animate-translateIn items-start gap-2'>
                <Avatar />
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <p className='text-[11px] font-semibold uppercase tracking-wide text-offGray'>
                    Cart price check
                  </p>
                  {rows.map((r, i) => (
                    <div
                      key={`${r.item.asin}-${i}`}
                      className='rounded-2xl border border-black/5 bg-white p-2.5 shadow-sm'
                    >
                      <p className='line-clamp-1 text-xs font-semibold text-primary-dark'>
                        {r.item.title ? decode(r.item.title) : r.item.asin}
                      </p>
                      <div className='mt-1 flex items-center justify-between gap-2 text-[11px]'>
                        <span className='text-offGray'>Cart {r.cartLabel}</span>
                        {r.best && r.savings > 0 ? (
                          <span className='font-semibold text-grassGreen'>
                            {titleCase(r.best.vendor)} {r.best.priceLabel} · save {fmtUsd(r.savings)}
                          </span>
                        ) : (
                          <span className='text-offGray'>best price ✓</span>
                        )}
                        {r.best?.item && r.savings > 0 && (
                          <button
                            type='button'
                            onClick={() => r.best?.item && addOne(r.best.item)}
                            disabled={added.has(r.best.item.asin)}
                            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
                              added.has(r.best.item.asin)
                                ? 'bg-grassGreen/15 text-grassGreen'
                                : 'bg-primary text-white hover:bg-primary-hover'
                            }`}
                          >
                            {added.has(r.best.item.asin) ? '✓' : 'Add'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className='text-xs font-bold text-primary-dark'>
                    {totalSavings > 0
                      ? `Potential savings: ${fmtUsd(totalSavings)}`
                      : 'Your cart already has the best prices.'}
                  </p>
                </div>
              </div>
            );
          }
          return m.role === 'user' ? (
            <div key={m.id} className='flex animate-translateIn justify-end'>
              <div className='max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-primary px-3.5 py-2.5 text-sm text-white shadow-sm'>
                {m.content}
              </div>
            </div>
          ) : (
            <div key={m.id} className='flex animate-translateIn items-start gap-2'>
              <Avatar />
              <div className='max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 text-sm text-primary-dark shadow-sm ring-1 ring-black/5'>
                {renderRich(m.content)}
              </div>
            </div>
          );
        })}

        {!busy &&
          (() => {
            const lastItems = messages[messages.length - 1]?.items;
            if (lastItems?.length) {
              return (
                <div className='flex flex-wrap gap-2 pl-10'>
                  <Chip onClick={() => lastItems.forEach((it) => addOne(it))}>Add all</Chip>
                  <Chip
                    onClick={() => {
                      const c = cheapestOf(lastItems);
                      if (c) addOne(c);
                    }}
                  >
                    Add cheapest
                  </Chip>
                  {cartCount > 0 && <Chip onClick={onSave}>Save ({cartCount})</Chip>}
                </div>
              );
            }
            if (cartCount > 0) {
              return (
                <div className='flex flex-wrap gap-2 pl-10'>
                  <Chip onClick={onSave}>Save ({cartCount})</Chip>
                  <Chip onClick={onClearCart}>Clear cart</Chip>
                </div>
              );
            }
            return null;
          })()}

        {busy && (
          <div className='flex items-start gap-2'>
            <Avatar />
            <div className='flex items-center gap-2 rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm ring-1 ring-black/5'>
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className='inline-block h-2 w-2 animate-bounce rounded-full bg-primary/60'
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
              {activity && <span className='ml-1 text-xs text-offGray'>{activity}</span>}
            </div>
          </div>
        )}

        {downloadProgress != null && (
          <p className='text-center text-xs text-offGray'>
            Downloading on-device model… {Math.round(downloadProgress * 100)}%
          </p>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={submit}
        className='flex items-center gap-2 border-t border-black/5 bg-white p-3'
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='Ask for anything…'
          className='h-11 flex-1 rounded-full bg-cloudGray px-4 text-sm text-primary-dark outline-none ring-1 ring-transparent transition focus:bg-white focus:ring-primary'
        />
        {busy ? (
          <button
            type='button'
            onClick={stop}
            aria-label='Stop'
            className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-dark text-white shadow-md transition hover:-translate-y-0.5'
          >
            <span className='material-icons-outlined text-[20px] leading-none'>stop</span>
          </button>
        ) : (
          <button
            type='submit'
            disabled={!input.trim()}
            aria-label='Send'
            className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dark text-white shadow-md transition hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-40'
          >
            <span className='material-icons-outlined text-[20px] leading-none'>arrow_upward</span>
          </button>
        )}
      </form>
    </div>
  );
};
